import _ from 'underscore';
import React from 'react';

import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { forTemplate } from '@trello/i18n';
import { getKey, Key, isForceSubmitEvent } from '@trello/keybindings';

import { Auth } from 'app/scripts/db/auth';
import { AutoInsertionView } from 'app/scripts/views/internal/autocomplete/AutoInsertionView';
import { CompleterUtil } from 'app/scripts/views/internal/autocomplete/CompleterUtil';

// require-ing the EditableView module because it is not converted to TS
// and importing results in many, many errors
const EditableView = require('app/scripts/views/internal/editable-view');

import TFM from 'app/scripts/lib/markdown/tfm';
import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import { l } from 'app/scripts/lib/localize';
import { hasSelectionIn } from 'app/scripts/lib/util/selection/has-selection-in';

import { ModelLoader } from 'app/scripts/db/model-loader';

import Layout from 'app/scripts/views/lib/layout';
import { markdownTracker } from 'app/scripts/lib/markdown/markdownTracker';
import { PopOver } from 'app/scripts/views/lib/pop-over';
import { FormattingHelpView } from 'app/scripts/views/card/FormattingHelpView';
import AttachmentViewer from 'app/scripts/views/internal/attachment-viewer';
import { friendlyLinks } from 'app/scripts/views/internal/friendly-links';
import { applyMarkdownShortcuts } from 'app/scripts/views/lib/markdown-transform';
import { applyBidiTooltip } from 'app/src/components/TrelloMarkdown/applyBidiTooltip';

import { Description, DescriptionProps } from 'app/src/components/Description';
import { renderComponent } from 'app/src/components/ComponentWrapper';

import type { ActionSubjectType } from '@trello/atlassian-analytics';

import type { Board } from 'app/scripts/models/board';
import type { Card } from 'app/scripts/models/card';
import type { Member } from 'app/scripts/models/member';
import type { Mention, DescriptionEditorProps } from '@trello/editor';

type EditorType = 'classic' | 'atlaskit';

interface TrelloMentionable {
  id: string;
  names: string[];
  type: string;
  text: string;
  extraText: string;
  mentionCount: number;
}

interface JQueryKeyboardEvent extends KeyboardEvent {
  originalEvent: KeyboardEvent;
  target: HTMLInputElement | null;
}

class DescriptionView extends EditableView {
  static initClass() {
    this.prototype.events = {
      'click .js-format-help': 'openFormatHelp',
      'click .js-edit-desc': 'editDesc',
      'click .js-edit-desc-button'() {
        return Analytics.sendClickedButtonEvent({
          buttonName: 'editDescriptionButton',
          source: this.source,
          containers: this.model.getAnalyticsContainers(),
        });
      },

      'keydown .field'(e: JQueryKeyboardEvent) {
        // Override the default keydown handler for EditableView (if we don't, it
        // will close PopOvers before we have a chance to check if one is open)
        //
        // (Handle Esc on keydown instead of keyup because we won't get a keydown
        // event if the Esc happened while an IME was open)

        // Discard the draft before saving on force submit
        // Otherwise, the component is rendered before the draft is discarded
        // in the async callback, thus re-saving the draft
        if (isForceSubmitEvent(e)) {
          this.commitEdits(e);
          // when an element, or its parent, gets set to `display: none`, different browsers handle the resulting focused element differently
          // To handle that, we manually set focus on the edit description button, which is the closest element to the textarea the user was just in before it was hidden
          this.$('.js-edit-desc-button').focus();
          return;
        }

        applyMarkdownShortcuts(e);

        if (getKey(e) === Key.Escape) {
          stopPropagationAndPreventDefault(e);

          if (AttachmentViewer.isActive()) {
            AttachmentViewer.clear();
            return;
          }

          if (PopOver.isVisible) {
            PopOver.hide();
            return;
          }

          // Save the drafts and put the model desc back in the draft
          // to prevent it from autosaving
          this.saveDrafts();
          this.$('.js-description-draft').val(this.model.get('desc'));

          Analytics.sendPressedShortcutEvent({
            shortcutName: 'escapeShortcut',
            keyValue: 'esc',
            source: this.source,
            containers: this.model.getAnalyticsContainers(),
            attributes: {
              whileEditingDescription: true,
            },
          });

          // Usually render fires on changes to the description, but we're
          // putting the model desc back in the description (see above) to
          // prevent the autosave. To show the "unsaved edits" dialog, we
          // have to call render here
          this.render();
          this.$el.find('.editing').removeClass('editing');
          return;
        }
      },

      'keyup .field'(e: KeyboardEvent) {
        if (getKey(e) === Key.Escape) {
          // Don't let the keyup propagate; we want to handle PopOvers differently;
          // (we wait until keydown so we won't cancel editing if a PopOver is open)
          stopPropagationAndPreventDefault(e);
        }

        return this.keyAutoMention(e);
      },

      'click .js-desc-editor'(e: MouseEvent) {
        if (this.isEditorEnabled) {
          this.handleEditorClickEvent(e);
        }
      },

      'keydown .js-desc-editor'(e: KeyboardEvent) {
        e.stopImmediatePropagation();

        if (isForceSubmitEvent(e)) {
          this.recordCardAction();
          this._discardDraft('desc');
          this.commitEdits(e);
        } else if (getKey(e) === Key.Escape) {
          this.saveDrafts();
          this.render();
          this.$el.find('.editing').removeClass('editing');
        }
      },

      'keyup .js-desc-editor'(e: KeyboardEvent) {
        e.stopImmediatePropagation();
        return;
      },

      'click .js-cancel-edit'(e: MouseEvent) {
        Analytics.sendClickedButtonEvent({
          buttonName: 'cancelEditDescriptionButton',
          source: this.source,
          containers: this.model.getAnalyticsContainers(),
        });

        return this.cancelEditDesc(e, 'by clicking on cancel button');
      },
      'mousedown .js-desc-content.is-hide-full'(e: MouseEvent) {
        stopPropagationAndPreventDefault(e);
        Analytics.sendClickedButtonEvent({
          buttonName: 'showFullDescriptionButton',
          source: this.source,
          containers: this.model.getAnalyticsContainers(),
        });

        return this.showFullDescription();
      },

      'click .js-desc'() {
        Analytics.sendUIEvent({
          action: 'clicked',
          actionSubject: 'input',
          actionSubjectId: 'descriptionInput',
          source: this.source,
          containers: this.model.getAnalyticsContainers(),
        });
      },
    };
  }

  initialize({
    board,
    card,
    maxDescLength,
    placeholderKey,
    source,
  }: {
    board: Board;
    card: Card;
    maxDescLength: number;
    placeholderKey: string;
    source: string;
  }) {
    this.traceId = null;
    this.isEditorReady = false;
    this.isEditorFailed = false;
    this.editorLoadError = null;
    this.hasInitiatedEditing = false;
    this.initPerfMeasurement();

    this.board = board;
    this.card = card;
    this.maxDescLength = maxDescLength;
    this.placeholderKey = placeholderKey;
    this.source = source;
    super.initialize(...arguments);

    this.hideFullDescription =
      this.model.get('desc')?.length > this.maxDescLength;
    this.descType = `${this.model.typeName.toLowerCase()} description`;

    this.isEditorEnabled = featureFlagClient.get('aaaa.web.editor', false);
    this.membersLastLoaded = {};

    // force those who have opted out of Editor back in by removing the override
    const shouldClearEditorOverride = featureFlagClient.get(
      'aaaa.web.clear-editor-overrides',
      false,
    );
    if (shouldClearEditorOverride) {
      featureFlagClient.removeOverride('aaaa.web.editor', false);
    }

    this.makeDebouncedMethods('render');

    this.listenTo(Layout, this.getAutosaveEvent(), this.onAutosaveEdit);
    return this.listenTo(this.model, 'change:desc', this.renderDebounced);
  }

  render() {
    const isCardDesc = this.model.typeName === 'Card';
    const props: DescriptionProps = {
      editable: this.model.editable(),
      placeholderKey: this.placeholderKey,
      isAdmin: false,
      isCardDesc,
      isTemplate: isCardDesc
        ? this.model.isOnBoardTemplate()
        : this.model.isTemplate(),
      isEditorEnabled: this.isEditorEnabled,
      editorProps: this.getEditorProps(),
      onEditorLoadError: this.sendTaskFailed.bind(this),
    };

    this.unmountDescriptionComponent = renderComponent(
      <Description {...props} />,
      this.$el[0],
    );

    const desc = this.model.get('desc');

    const descHtml = TFM.description.format(desc || '', {
      textData: this.model.get('descData'),
    }).output;
    const $desc = this.$('.js-desc');
    $desc.empty().append(descHtml).data('unmarkeddown', desc);

    applyBidiTooltip($desc[0]);

    $desc.find('p').attr('dir', 'auto');
    friendlyLinks($desc[0], this.board, {
      analyticsContext: {
        source: this.source,
        containers: this.model.getAnalyticsContainers(),
        attributes: {
          fromSection: 'description',
        },
      },
    });

    const hasDesc = !/^\s*$/.test(descHtml);
    this.$('.js-show-with-desc').toggleClass('hide', !hasDesc);
    this.$('.js-hide-with-desc').toggleClass('hide', hasDesc);

    this.$('.js-desc-content').toggleClass(
      'is-hide-full',
      this.hideFullDescription,
    );

    this.handleDraft();
    this.renderFormattingButton();
    this.sendPerfMeasurement('classic');

    return this;
  }

  handleDraft() {
    const desc = this.model.get('desc');
    const draft = this.$('.js-description-draft').val();
    const hasDraft = draft?.length > 0 && draft !== desc;
    this.$('.js-description-draft').addClass(
      this.descType.split(' ').join('-'),
    );
    this.$('.js-hide-with-draft').toggleClass('hide', hasDraft);
    this.recallDrafts();
  }

  remove() {
    if (this.unmountDescriptionComponent) {
      this.unmountDescriptionComponent();
      this.unmountDescriptionComponent = null;
    }
    this.perfMeasurement = {};
    return super.remove(...arguments);
  }

  initPerfMeasurement() {
    this.perfMeasurement = {
      start: performance.now(),
      classic: null,
      atlaskit: null,
    };
  }

  sendPerfMeasurement(type: EditorType) {
    if (this.perfMeasurement[type] || !this.perfMeasurement.start) {
      return;
    }

    const duration = performance.now() - this.perfMeasurement.start;

    Analytics.sendOperationalEvent({
      action: 'initialized',
      actionSubject: 'editor',
      source: this.source,
      attributes: {
        time: duration,
        type: type,
      },
    });
    this.perfMeasurement[type] = duration;
  }

  getEditorProps(): DescriptionEditorProps {
    if (!this.isEditorEnabled) {
      return {};
    }

    const format = forTemplate('description');
    const placeholder = format(this.placeholderKey);

    const hasDraft = this.$('.edits-warning').is(':visible');

    const $field = this.$('.field');
    const draft = $field.val();
    const desc = this.model.get('desc');

    const mentionData = this.getMentionDataForEditor();

    const props = {
      description: hasDraft || this.isEditing() ? draft : desc,
      placeholder,
      shouldFocus: false,
      mentionData,
      // eslint-disable-next-line react/jsx-no-bind
      onMentionSelect: this.onMentionSelect.bind(this),
      // eslint-disable-next-line react/jsx-no-bind
      onEditorReady: this.onEditorReady.bind(this),
      // eslint-disable-next-line react/jsx-no-bind
      onEditorChange: this.onEditorChange.bind(this),
    };

    return props;
  }

  // We need to stop click events within the Editor field from propagating up
  // to the root element and dismissing edit mode.
  handleEditorClickEvent(e: MouseEvent) {
    const $target = this.$(e.target);

    const openLinkElem = $target.closest('.hyperlink-open-link')[0];
    const proseMirrorElem = $target.closest('.ProseMirror')[0];

    if (openLinkElem || proseMirrorElem) {
      return;
    }

    stopPropagationAndPreventDefault(e);
  }

  getMentionDataForEditor() {
    const getTrelloMember = (m: Member) => {
      return {
        id: m.get('id'),
        avatarUrl: m.get('avatarUrl'),
        fullName: m.get('fullName'),
        username: m.get('username'),
      };
    };

    const currentMember = getTrelloMember(Auth.me());
    const isCard = this.model.typeName === 'Card';

    const card = isCard ? this.model : null;
    const board = isCard ? card.getBoard() : this.model;
    const org = board.getOrganization() || undefined;

    // This logic was duplicated from the getAutoMentionerView method from
    // completer_util.js
    if (org) {
      const FIVE_MINUTES = 5 * 60 * 1000;
      const dateLastLoaded = this.membersLastLoaded[org.id];
      if (!dateLastLoaded || Date.now() - dateLastLoaded > FIVE_MINUTES) {
        this.membersLastLoaded[org.id] = Date.now();
        // This loads org members async, and then we manually calls render()
        // when the org data is loaded in order to update the Mention Provider.
        ModelLoader.loadOrganizationMembersData(org.id).then(() => {
          this.render();
        });
      }
    }

    const autoMentionerView = this.autoMentionerView();

    autoMentionerView.board = board;
    autoMentionerView.card = card;

    const mentionables: TrelloMentionable[] = autoMentionerView.getMentionables.call(
      autoMentionerView,
    );

    const specialMentions = mentionables.filter((m) => {
      if (m.type === 'extra' && m.text === 'board') return true;
      if (m.type === 'extra' && m.text === 'card') return isCard;
      return false;
    });

    // There's an issue with special mentions, if the memberships are updated
    // after the Editor has rendered, the Editor does not update the Mention
    // Provider. So the counts in these special mentions can become stale.
    // Currently, there is no workaround for this.

    const memberList = mentionables
      .filter((m) => m.type === 'member')
      .map((m) => {
        const member = this.modelCache.get('Member', m.id);
        return getTrelloMember(member);
      });

    return {
      memberList,
      currentMember,
      specialMentions,
    };
  }

  sendTaskStart() {
    if (!this.isEditorEnabled) {
      return;
    }
    if (this.traceId) {
      return;
    }
    const subtype = this.model.typeName === 'Card' ? 'card' : 'board';
    this.traceId = Analytics.startTask({
      taskName: `view-editor/description/${subtype}`,
      source: this.source,
    });

    // If the Editor is already initialized when entering edit mode,
    // go ahead and send the success event too.
    if (this.isEditorReady) {
      this.sendTaskSuccess();
    }

    // If the Editor has already failed to load when entering edit mode,
    // go ahead and send the failed event too.
    if (this.isEditorFailed) {
      this.sendTaskFailed();
    }
  }

  sendTaskSuccess() {
    if (!this.isEditorEnabled) {
      return;
    }
    if (!this.traceId) {
      return;
    }
    const subtype = this.model.typeName === 'Card' ? 'card' : 'board';
    Analytics.taskSucceeded({
      taskName: `view-editor/description/${subtype}`,
      source: this.source,
      traceId: this.traceId,
    });
  }

  sendTaskFailed(error?: Error) {
    if (!this.isEditorEnabled) {
      return;
    }

    this.isEditorFailed = true;

    if (error) {
      this.editorLoadError = error;
    }

    if (!this.traceId || !this.editorLoadError) {
      return;
    }
    const subtype = this.model.typeName === 'Card' ? 'card' : 'board';
    Analytics.taskFailed({
      taskName: `view-editor/description/${subtype}`,
      source: this.source,
      traceId: this.traceId,
      error: this.editorLoadError,
    });
  }

  onEditorReady() {
    this.isEditorReady = true;
    this.sendPerfMeasurement('atlaskit');
    this.sendTaskSuccess();
  }

  // Sync any changes to the Editor contents back to the EditableView field
  // which is still rendered, but is hidden via CSS.
  onEditorChange(val: string) {
    if (this.hasInitiatedEditing) {
      this.$('.field').val(val);
    }
  }

  onMentionSelect(mention: Mention) {
    const { mentionName, id } = mention;

    const isCard = this.model.typeName === 'Card';

    const cardId = isCard ? this.model.get('id') : undefined;
    const boardId = isCard
      ? this.model.getBoard().get('id')
      : this.model.get('id');

    let actionSubject: ActionSubjectType = 'member';
    let actionSubjectId = id;

    let source = getScreenFromUrl();

    // if the mention is coming from the board screen,
    // it's coming from the Description box in the boards menu
    if (source === 'boardScreen') {
      source = 'boardMenuDrawerAboutScreen';
    }

    if (mentionName === 'board') {
      actionSubject = 'board';
      actionSubjectId = boardId;
    } else if (mentionName === 'card') {
      actionSubject = 'card';
      actionSubjectId = cardId;
    }

    Analytics.sendTrackEvent({
      action: 'mentioned',
      actionSubject,
      actionSubjectId,
      source,
      containers: this.model.getAnalyticsContainers(),
    });
  }

  // This is a bit of a hack, but when the user goes to edit the description,
  // we need to manually to move focus to the Editor input. By default, the
  // entire contents of the Editor input get selected, so this function
  // manually moves the cursor to the end of the content.
  focusEditor() {
    if (this.isEditorEnabled) {
      const el = this.$('.ProseMirror[contenteditable]');
      if (el.length) {
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el[0]);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }

  showFullDescription() {
    this.hideFullDescription = false;
    this.$('.js-desc-content').removeClass('is-hide-full');
  }

  edit(attr: string, focus?: boolean) {
    this.sendTaskStart();
    super.edit(attr, focus);
    this.focusEditor();
    this.hasInitiatedEditing = true;
  }

  editDesc(e: MouseEvent) {
    stopPropagationAndPreventDefault(e);
    this.showFullDescription();
    this.edit('desc');

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'input',
      actionSubjectId: 'descriptionInput',
      source: this.source,
      containers: this.model.getAnalyticsContainers(),
    });
  }

  // Duplicating logic from EditableView because the editField method returns
  // early if the click is within a ".js-react-root" element. Now that the
  // Description template has been converted to React, this causes the click
  // handler on the description to fail to initiate editing.
  editField(e: MouseEvent) {
    const $link = this.$(e.target).closest('a');
    if ($link.length > 0 && $link.attr('href') !== '#') {
      return;
    }
    const $container = this.$(e.target).closest('.editable');
    if ($container.length === 0) {
      return;
    }

    // If they've selected some text, they're probably trying to copy it
    if (hasSelectionIn($container)) {
      return;
    }

    stopPropagationAndPreventDefault(e);
    PopOver.hide();

    const attr = $container.attr('attr');
    this.edit(attr);
  }

  cancelEditDesc(e: MouseEvent, method: string) {
    const fromEscape = method.includes('escape key');
    Analytics.sendTrackEvent({
      action: 'cancelled',
      actionSubject: 'descriptionEdit',
      source: this.source,
      containers: this.model.getAnalyticsContainers(),
      attributes: {
        method: fromEscape ? 'escapeKey' : 'cancelEditDescriptionButton',
      },
    });

    this.hasInitiatedEditing = false;
    return this.clearEdits(e);
  }

  clearEdits(e: Event) {
    super.clearEdits(...arguments);
    this.render();
  }

  expandDesc(e: Event) {
    stopPropagationAndPreventDefault(e);
    this.showFullDescription();
    this.sendTaskStart();
    this.edit('desc', false);
  }

  renderFormattingButton() {
    const localizedText = l(['view title', 'formatting help']);
    // Wrap button with longer button text only on board sidebar, where there's less horizontal space
    const wrapButton =
      this.descType === 'board description' && localizedText.length > 20;
    this.$('.description-edit').toggleClass('long', wrapButton);
    return this.$('.description-edit .helper').toggleClass('long', wrapButton);
  }

  openFormatHelp(e: MouseEvent) {
    Analytics.sendClickedButtonEvent({
      buttonName: 'formattingHelpDescriptionButton',
      source: this.source,
      containers: this.model.getAnalyticsContainers(),
    });
    PopOver.toggle({
      elem: this.$('.js-format-help'),
      view: FormattingHelpView,
      options: { model: this.model, modelCache: this.modelCache },
      keepEdits: true,
    });
    return false;
  }

  // Autosave

  getAutosaveEvent() {
    return `autosave${this.model.typeName}Description`;
  }

  onAutosaveEdit($target: JQuery) {
    this.hideFullDescription = false;
    return this.commitEdits(null, $target);
  }

  isEditing() {
    return !!this.$('.description-title .editing').length;
  }

  commitEdits(e: Event | null, target: JQuery | null) {
    if (this.isEditing()) {
      // Validate current input before removing "editing" class
      if (!this.validate()) {
        return;
      }

      // @ts-expect-error
      Auth.me().editing({ idBoard: this.board.id });
      // Show 'Edit' button upon saving description
      this.$('.description-title .editing').removeClass('editing');

      if (!this.card && this.board) {
        this.recordBoardAction();
      } else {
        this.recordCardAction();
      }

      // Discard the draft before autosaving
      // Otherwise, the component is rendered before the draft is discarded
      // in the async callback, thus re-saving the draft
      this._discardDraft('desc');

      return super.commitEdits(...arguments);
    }
  }

  recordBoardAction() {
    if (this.model.typeName !== 'Board') {
      return;
    }
    const description = this.$('.js-description-draft').val();
    const previousDescription = this.model.get('desc');
    if (previousDescription !== description) {
      Analytics.sendUpdatedBoardFieldEvent({
        field: 'description',
        source: this.source,
        containers: this.model.getAnalyticsContainers(),
      });
      markdownTracker(description, 'description');
    }
  }

  recordCardAction() {
    if (this.model.typeName !== 'Card') {
      return;
    }
    const description = this.$('.js-description-draft').val();
    const previousDescription = this.model.get('desc');
    if (previousDescription !== description) {
      this.model.recordAction({
        type: 'update-description',
        description,
        previousDescription,
      });
      markdownTracker(description, 'description');
    }
  }

  // Auto Completer

  getTextInput() {
    const descInput = this.$('.js-description-draft:visible');
    if (descInput.length) {
      return descInput;
    }
  }

  getMentionTarget() {
    const descInput = this.$('.js-description-draft:visible');
    if (descInput.length) {
      return descInput[0];
    }
  }

  getEmojiTarget() {
    const descInput = this.$('.js-description-draft:visible');
    if (descInput.length) {
      return descInput[0];
    }
  }
}
DescriptionView.initClass();

_.extend(DescriptionView.prototype, AutoInsertionView, CompleterUtil);

export { DescriptionView };
